(() => {
  const updateCartCount = async () => {
    const rootPath = window.Shopify?.routes?.root || '/';

    try {
      const response = await fetch(`${rootPath}cart.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return;

      const cart = await response.json();
      const countTargets = document.querySelectorAll(
        '[data-cart-count], .cart-count-bubble span[aria-hidden="true"]'
      );

      countTargets.forEach((target) => {
        target.textContent = cart.item_count;
      });

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart }
        })
      );
    } catch (error) {
      // The item is already in the cart; a counter refresh failure should not
      // turn a successful add into an error for the shopper.
    }
  };

  const initQuickAdd = (form) => {
    if (!form || form.dataset.pvQuickAddInitialized === 'true') return;

    const select = form.querySelector('[data-pv-variant-select]');
    const productCard = form.closest('[data-pv-product-card]');
    const price = productCard?.querySelector('[data-pv-variant-price]');
    const productImage = productCard?.querySelector('.pv-vibe-product__image');
    const button = form.querySelector('[data-pv-add-button]');
    const buttonLabel = form.querySelector('[data-pv-add-label]');
    const status = form.querySelector('[data-pv-add-status]');
    const initialLabel = buttonLabel?.textContent.trim() || 'ADD TO CART';
    const successLabel = form.dataset.successLabel || 'ADDED ✓';
    let resetTimer;

    if (!button) return;

    select?.addEventListener('change', () => {
      const selectedOption = select.options[select.selectedIndex];

      if (price && selectedOption?.dataset.price) {
        price.textContent = selectedOption.dataset.price;
      }

      if (
        productImage?.tagName === 'IMG' &&
        selectedOption?.dataset.imageSrc
      ) {
        if (selectedOption.dataset.imageSrcset) {
          productImage.srcset = selectedOption.dataset.imageSrcset;
        } else {
          productImage.removeAttribute('srcset');
        }

        productImage.src = selectedOption.dataset.imageSrc;
        productImage.alt = selectedOption.dataset.imageAlt || productImage.alt;
      }

      window.clearTimeout(resetTimer);
      button.classList.remove('is-loading', 'is-added');
      button.removeAttribute('aria-busy');
      button.disabled = false;
      if (buttonLabel) buttonLabel.textContent = initialLabel;

      if (status) {
        status.textContent = '';
        status.classList.remove('is-success', 'is-error');
      }
    });

    form.addEventListener('submit', async (event) => {
      if (!window.fetch || button.disabled) return;

      event.preventDefault();
      window.clearTimeout(resetTimer);

      button.disabled = true;
      button.classList.remove('is-added');
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      if (buttonLabel) buttonLabel.textContent = 'ADDING…';
      if (status) {
        status.textContent = '';
        status.classList.remove('is-success', 'is-error');
      }

      try {
        const formData = new FormData(form);
        const cartUi =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        if (cartUi && typeof cartUi.getSectionsToRender === 'function') {
          const sectionIds = cartUi.getSectionsToRender().map((section) => section.id);
          formData.append('sections', sectionIds);
          formData.append('sections_url', window.location.pathname);
        }

        const rootPath = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${rootPath}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });
        const result = await response.json();

        if (!response.ok || result.status) {
          throw new Error(result.description || 'This option could not be added.');
        }

        if (cartUi && typeof cartUi.renderContents === 'function' && result.sections) {
          cartUi.renderContents(result);
        } else {
          await updateCartCount();
        }

        button.classList.remove('is-loading');
        button.classList.add('is-added');
        if (buttonLabel) buttonLabel.textContent = successLabel;
        if (status) {
          status.textContent = 'Added to your cart.';
          status.classList.add('is-success');
        }

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:item-added', {
            bubbles: true,
            detail: { product: result }
          })
        );

        resetTimer = window.setTimeout(() => {
          button.classList.remove('is-added');
          if (buttonLabel) buttonLabel.textContent = initialLabel;
          if (status) {
            status.textContent = '';
            status.classList.remove('is-success');
          }
        }, 2400);
      } catch (error) {
        button.classList.remove('is-loading', 'is-added');
        if (buttonLabel) buttonLabel.textContent = initialLabel;
        if (status) {
          status.textContent = error.message;
          status.classList.add('is-error');
        }
      } finally {
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }
    });

    form.dataset.pvQuickAddInitialized = 'true';
  };

  const initVibeFinder = (root) => {
    if (!root || root.dataset.pvInitialized === 'true') return;

    const buttons = [...root.querySelectorAll('[data-pv-vibe-choice]')];
    const cards = [...root.querySelectorAll('[data-pv-vibe-card]')];
    const status = root.querySelector('[data-pv-vibe-status]');

    root.querySelectorAll('[data-pv-quick-add]').forEach(initQuickAdd);

    if (!buttons.length || !cards.length) {
      root.dataset.pvInitialized = 'true';
      return;
    }

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const revealCharacter = (button, shouldScroll = true) => {
      const targetId = button.dataset.target;
      const target = document.getElementById(targetId);

      if (!target) return;

      buttons.forEach((choice) => {
        choice.setAttribute('aria-pressed', choice === button ? 'true' : 'false');
      });

      cards.forEach((card) => card.classList.toggle('is-match', card === target));

      if (status) {
        status.textContent = `${button.dataset.character} looks like your vibe.`;
      }

      if (shouldScroll) {
        target.scrollIntoView({
          behavior: reducedMotion ? 'auto' : 'smooth',
          block: 'center'
        });
      }

      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', `#${targetId}`);
      }
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => revealCharacter(button));
    });

    const hash = window.location.hash.replace('#', '');
    const initialButton = buttons.find((button) => button.dataset.target === hash);
    if (initialButton) revealCharacter(initialButton, false);

    root.dataset.pvInitialized = 'true';
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-pv-vibe-finder]').forEach(initVibeFinder);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
